import React, { useEffect, useCallback } from 'react';
import { Stack } from 'react-bootstrap';
import { useGetPostQuery } from 'services';
import useFeedsForYouStore, {
	setForYouFeeds,
	setForYouFeedsMeta,
	setForYouLastcursor,
	setForYouLastScrolling,
	setForYouCurrentCursor,
} from 'store/feedsForYou';
import { dataLimits } from 'constants/index';
import InfiniteScroll from 'react-infinite-scroll-component';
import { SocialPostSkeletonList } from 'components/Common/Skeleton/SocialPostSkeleton';
import AdvertisementPost from 'components/SocialPost/AdvertisementPost';
import SocialPost from 'components/SocialPost';
import useAuthStore from 'store/auth';
import useTranslate from 'hooks/useTranslate';

const ForYouFeedList: React.FC = () => {
	const { translate } = useTranslate();

	const {
		feeds: feedsList = [],
		feedsMeta,
		lastScrolling,
		lastcursor,
		currentCusror,
	}: Record<string, any> = useFeedsForYouStore((state) => state);

	const { token: isAuthenticated }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);

	// For You uses allUsers=true and connectionType=all
	const searchString = `order=DESC&cursor=${currentCusror || 0}&limit=${dataLimits?.feedsListing}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected&allUsers=true`;

	const {
		data: { docs: postList = {}, meta = {} } = {},
		status,
		isStale,
		refetch,
	} = useGetPostQuery({
		searchString: searchString,
		userToken: isAuthenticated,
		isFetch: lastcursor === currentCusror ? false : true,
	});

	useEffect(() => {
		if (typeof refetch === 'function') {
			refetch();
		}
	}, [isAuthenticated]);

	const loadMoreFunc = useCallback(() => {
		setForYouLastcursor(0);
	}, [feedsMeta]);

	const handleScroll = useCallback((event: any) => {
		const scrollTop = event.target.scrollTop;
		setForYouLastScrolling(scrollTop);
	}, []);

	useEffect(() => {
		if (
			status === 'success' &&
			postList &&
			Array.isArray(postList.results) &&
			postList.results.length > 0 &&
			isStale
		) {
			setForYouFeeds([...feedsList, ...postList.results]);
			setForYouFeedsMeta(meta);
			setForYouCurrentCursor(meta?.nextCursor || 0);
			setForYouLastcursor(meta?.nextCursor || 0);
		}
	}, [postList, status]);

	useEffect(() => {
		const scrollableDiv = document.getElementById('infiniteDiv-for-you-feeds');
		if (scrollableDiv) {
			scrollableDiv.scrollTop = lastScrolling;
		}
	}, [lastScrolling, feedsList.length]);

	const refreshFeeds = useCallback(() => {
		refetch().then((res: any) => {
			if (res?.isSuccess) {
				setForYouFeeds(res?.data?.docs?.results);
				setForYouFeedsMeta(res?.data?.meta);
				setForYouCurrentCursor(res?.data?.meta?.nextCursor || 0);
				setForYouLastcursor(res?.data?.meta?.nextCursor || 0);
			}
		});
	}, [refetch]);

	return (
		<>
			{feedsList?.length > 0 ? (
				<InfiniteScroll
					dataLength={feedsList.length}
					next={loadMoreFunc}
					hasMore={feedsMeta?.hasMore}
					loader={feedsList.length >= 1 && <SocialPostSkeletonList count={3} noButton={true} />}
					scrollableTarget="infiniteDiv-for-you-feeds right-area"
					scrollThreshold="300px"
					refreshFunction={loadMoreFunc}
					pullDownToRefreshThreshold={50}
					onScroll={handleScroll}
					pullDownToRefreshContent={
						<h3 style={{ textAlign: 'center' }}>
							&#8595; {translate('Common.pullDownToRefresh')}
						</h3>
					}
					releaseToRefreshContent={
						<h3 style={{ textAlign: 'center' }}>
							&#8593; {translate('Common.releaseToRefresh')}
						</h3>
					}
				>
					{feedsList.length > 0 ? (
						feedsList.map((post: any, index: number) =>
							post?.isAdvertisement ? (
								<AdvertisementPost
									post={post}
									key={`for-you-${post?.postId}-${index}-${post?.userId}`}
								/>
							) : (
								<SocialPost
									post={post}
									key={`for-you-${post?.postId}-${index}-${post?.userId}`}
									refetch={refreshFeeds}
								/>
							),
						)
					) : (
						<Stack className="fw-bold">{translate('Common.noPostFound')}</Stack>
					)}
				</InfiniteScroll>
			) : (
				<>
					{status === 'pending' ? (
						<SocialPostSkeletonList count={3} noButton={true} />
					) : (
						<Stack className="fw-bold">{translate('Common.noPostFound')}</Stack>
					)}
				</>
			)}
		</>
	);
};

export default ForYouFeedList;
